import { styled } from '@mui/material';
import {
  backgroundLineEntranceAnimation,
  completedProgressAnimations,
  currentProgressAnimations,
  errorProgressAnimations,
} from '../animations/progress-animations';
import { PROGRESS_LINE_CONSTANTS } from '../constants/progress-line.constants';

interface JSProgressLineProps {
  left: number;
  width: number;
}

interface JSCurrentSegmentProgressLineProps extends JSProgressLineProps {
  isInitialMount?: boolean;
}

interface JSErrorProgressLineProps extends JSProgressLineProps {
  isInitialMount?: boolean;
}

/**
 * Base styles for all JavaScript-positioned progress lines
 */
const getBaseJSProgressLineStyles = (_theme: unknown) => ({
  position: 'absolute' as const,
  top: PROGRESS_LINE_CONSTANTS.VERTICAL_POSITION,
  height: PROGRESS_LINE_CONSTANTS.HEIGHT,
  borderRadius: PROGRESS_LINE_CONSTANTS.BORDER_RADIUS,
  transform: 'translateY(-50%)',
  // No default z-index - will be set per component
});

/**
 * Background progress line using JavaScript positioning
 * Positioned from first node center to last node center
 */
export const JSProgressLine = styled('div')<JSProgressLineProps>(
  ({ theme, left, width }) => ({
    ...getBaseJSProgressLineStyles(theme),
    left: `${left}px`,
    width: `${width}px`,
    backgroundColor: theme.palette.text.fifth,
    animation: `backgroundLineEntrance ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.BACKGROUND_ENTRANCE.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.BACKGROUND_ENTRANCE.EASING}`,
    transformOrigin: 'left center', // Ensure animation scales from left to right
    zIndex: '0 !important', // Lowest - background line at the bottom (nodes are z-index 20)
    ...backgroundLineEntranceAnimation,
  }),
);

/**
 * Completed progress line using JavaScript positioning
 * Shows completed segments in normal color
 */
export const JSCompletedProgressLine = styled('div')<JSProgressLineProps>(
  ({ theme, left, width }) => ({
    ...getBaseJSProgressLineStyles(theme),
    left: `${left}px`,
    // Start with width 0 and let animation handle the final width
    width: 0,
    background: `linear-gradient(90deg, 
      ${theme.palette.secondaryInvert.main} 0%, 
      ${theme.palette.secondaryInvert.main} 100%
    )`,
    animation: `progressFillCompleted ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.COMPLETED_FILL.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.COMPLETED_FILL.EASING} forwards`,
    // Use CSS custom property to pass the target width to the animation
    '--target-width': `${width}px`,
    boxShadow: `0 0 ${PROGRESS_LINE_CONSTANTS.SHADOWS.GLOW_BLUR}px ${theme.palette.secondaryInvert.main}${PROGRESS_LINE_CONSTANTS.OPACITY.SHADOW_NORMAL}`,
    zIndex: '1 !important', // Middle - above background, below nodes (nodes are z-index 20)
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      right: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      bottom: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      background: `linear-gradient(90deg,
        transparent 0%,
        ${theme.palette.secondaryInvert.main}${PROGRESS_LINE_CONSTANTS.OPACITY.SHINE_NORMAL} 50%,
        transparent 100%
      )`,
      borderRadius: PROGRESS_LINE_CONSTANTS.BORDER_RADIUS,
      animation: `progressShine ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.EASING} infinite ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.DELAY}`,
      zIndex: '-1 !important',
    },
    ...completedProgressAnimations,
  }),
);

/**
 * Current segment progress line using JavaScript positioning
 * Shows progress within the current active segment
 */
export const JSCurrentSegmentProgressLine = styled(
  'div',
)<JSCurrentSegmentProgressLineProps>(
  ({ theme, left, width, isInitialMount = false }) => ({
    ...getBaseJSProgressLineStyles(theme),
    left: `${left}px`,
    // Conditional styling based on whether this is initial mount or real-time update
    ...(isInitialMount
      ? {
          // On mount: Start from 0 and animate with delay like completed progress bar
          width: 0,
          animation: `progressFillCurrent ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.CURRENT_FILL.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.CURRENT_FILL.EASING} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.CURRENT_FILL.DELAY} forwards`,
          '--target-width': `${width}px`,
        }
      : {
          // Real-time updates: Use actual width with smooth transition
          width: `${width}px`,
          transition: `width ${PROGRESS_LINE_CONSTANTS.TRANSITIONS.DURATION} ${PROGRESS_LINE_CONSTANTS.TRANSITIONS.EASING}`,
        }),
    background: `linear-gradient(90deg, 
      ${theme.palette.secondaryInvert.main} 0%, 
      ${theme.palette.secondaryInvert.main} 100%
    )`,
    boxShadow: `0 0 ${PROGRESS_LINE_CONSTANTS.SHADOWS.GLOW_BLUR}px ${theme.palette.secondaryInvert.main}${PROGRESS_LINE_CONSTANTS.OPACITY.SHADOW_ACTIVE}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      right: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      bottom: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      background: `linear-gradient(90deg,
        transparent 0%,
        ${theme.palette.secondaryInvert.main}${PROGRESS_LINE_CONSTANTS.OPACITY.SHINE_NORMAL} 50%,
        transparent 100%
      )`,
      borderRadius: PROGRESS_LINE_CONSTANTS.BORDER_RADIUS,
      animation: `progressShine ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.EASING} infinite ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.CURRENT_FILL.DELAY}`,
      zIndex: '-1 !important',
    },
    zIndex: '1 !important', // Middle - above background, below nodes (nodes are z-index 20)
    ...currentProgressAnimations,
  }),
);

/**
 * Error progress line using JavaScript positioning
 * Shows error state with red color
 * Supports dual animation system: keyframe for initial mount, transition for real-time updates
 */
export const JSErrorProgressLine = styled('div')<JSErrorProgressLineProps>(
  ({ theme, left, width, isInitialMount = false }) => ({
    ...getBaseJSProgressLineStyles(theme),
    left: `${left}px`,
    background: `linear-gradient(90deg, 
      ${theme.palette.error.main} 0%, 
      ${theme.palette.error.main} 100%
    )`,
    boxShadow: `0 0 ${PROGRESS_LINE_CONSTANTS.SHADOWS.GLOW_BLUR}px ${theme.palette.error.main}${PROGRESS_LINE_CONSTANTS.OPACITY.SHADOW_ACTIVE}`,

    // Dual animation system: keyframe for initial mount/step changes, transition for PNL updates
    ...(isInitialMount
      ? {
          // Initial mount: start from 0 and animate with keyframe
          width: 0,
          '--target-width': `${width}px`,
          animation: `progressFillError ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.ERROR_FILL.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.ERROR_FILL.EASING} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.ERROR_FILL.DELAY} forwards`,
        }
      : {
          // Real-time updates: use actual width with smooth transition
          width: `${width}px`,
          transition: `width ${PROGRESS_LINE_CONSTANTS.TRANSITIONS.DURATION} ${PROGRESS_LINE_CONSTANTS.TRANSITIONS.EASING}`,
        }),

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      right: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      bottom: PROGRESS_LINE_CONSTANTS.PSEUDO_OFFSET,
      background: `linear-gradient(90deg,
        transparent 0%,
        ${theme.palette.error.main}${PROGRESS_LINE_CONSTANTS.OPACITY.SHINE_NORMAL} 50%,
        transparent 100%
      )`,
      borderRadius: PROGRESS_LINE_CONSTANTS.BORDER_RADIUS,
      animation: `progressShine ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.DURATION} ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.SHINE.EASING} infinite ${PROGRESS_LINE_CONSTANTS.ANIMATIONS.ERROR_FILL.DELAY}`,
      zIndex: '-1 !important',
    },
    zIndex: '1 !important', // Middle - above background, below nodes (nodes are z-index 20)
    ...errorProgressAnimations,
  }),
);
